import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROLE_LABEL, USER_STATUS_LABEL } from '@doit/shared';
import { ApiError, http } from '../api/client';
import { useAuth, useCurrentUser } from '../api/auth';
import { Alert, Button, Card, Field, PageHeader } from '../components/ui';
import { formatDateTime } from '../lib/format';

export function ProfilePage() {
  const user = useCurrentUser();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    if (next !== confirm) {
      setError('ახალი პაროლები არ ემთხვევა');
      return;
    }

    setBusy(true);
    try {
      await http.post('/auth/change-password', { currentPassword: current, newPassword: next });
      setDone(true);
      // პაროლის ცვლილებისას სერვერი ყველა სესიას აუქმებს
      setTimeout(() => {
        void logout().then(() => navigate('/login', { replace: true }));
      }, 1800);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFieldErrors(err.details ?? {});
      } else {
        setError('პაროლის შეცვლა ვერ მოხერხდა');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title="ჩემი პროფილი" subtitle="ანგარიშის მონაცემები და უსაფრთხოება" />

      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="მონაცემები">
          <dl className="divide-y divide-line text-[13px]">
            <Row label="სახელი გვარი" value={user.fullName} />
            <Row label="ელფოსტა" value={user.email} />
            <Row label="როლი" value={ROLE_LABEL[user.role]} />
            <Row label="სტატუსი" value={USER_STATUS_LABEL[user.status]} />
            <Row label="ტელეფონი" value={user.phone ?? '—'} />
            <Row label="ბოლო შესვლა" value={formatDateTime(user.lastLoginAt)} />
          </dl>
          <p className="mt-4 text-[12px] text-ink-mute">
            მონაცემების შესაცვლელად მიმართე ადმინისტრატორს.
          </p>
        </Card>

        <Card title="პაროლის შეცვლა">
          {done ? (
            <Alert kind="success">
              პაროლი შეიცვალა. უსაფრთხოებისთვის ყველა სესია გაუქმდა — გადამისამართდები შესვლის
              გვერდზე.
            </Alert>
          ) : (
            <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
              {error && <Alert kind="error">{error}</Alert>}

              <Field
                label="მიმდინარე პაროლი"
                type="password"
                autoComplete="current-password"
                required
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                error={fieldErrors.currentPassword}
              />
              <Field
                label="ახალი პაროლი"
                type="password"
                autoComplete="new-password"
                required
                value={next}
                onChange={(e) => setNext(e.target.value)}
                error={fieldErrors.newPassword}
                hint="მინიმუმ 8 სიმბოლო, დიდი და პატარა ლათინური ასო, ციფრი"
              />
              <Field
                label="გაიმეორე ახალი პაროლი"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                error={confirm && confirm !== next ? 'პაროლები არ ემთხვევა' : undefined}
              />

              <Button type="submit" loading={busy}>
                პაროლის შეცვლა
              </Button>
            </form>
          )}
        </Card>
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 py-2.5">
      <dt className="text-ink-mute">{label}</dt>
      <dd className="text-right font-medium text-ink">{value}</dd>
    </div>
  );
}
